import { useState } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { Card, CardHeader, Button, Alert, Spinner } from '../../components/UI';
import { useClients } from '../../hooks/useClients';
import { resolveClientAccounts } from '../../utils/clientAccounts';
import {
  CostScope,
  CostExportPreview,
  previewCostExport,
  generateCostExport,
} from '../../services/costExports';
import { CurrencyDollarIcon, DocumentArrowDownIcon, EyeIcon } from '@heroicons/react/24/outline';

type Route = '/' | '/callback' | '/reports' | '/network' | '/costs';

interface CostsPageProps {
  onNavigate?: (route: Route) => void;
}

const fmt = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function CostsPage({ onNavigate }: CostsPageProps) {
  const { clients, loading: clientsLoading } = useClients();
  const [clientId, setClientId] = useState('');
  const [scope, setScope] = useState<CostScope>('ORG');
  const [accountLabel, setAccountLabel] = useState('');
  const [period, setPeriod] = useState<'previous' | 'current'>('previous');
  // Margen (%) para el reporte. Se precarga con el del cliente; editable para este reporte.
  const [margin, setMargin] = useState<string>('');

  const [preview, setPreview] = useState<CostExportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const selectedClient = clients.find((c) => c.id === clientId);
  const accounts = selectedClient ? resolveClientAccounts(selectedClient) : [];
  const hasMpa = accounts.some((a) => a.isMpaAccount);

  const buildRequest = () => ({
    clientId,
    scope,
    accountLabel: scope === 'ACCOUNT' ? accountLabel : undefined,
    period,
    // Si el campo está vacío, enviamos undefined (usa el margen del cliente).
    marginOverride: margin.trim() === '' ? undefined : Number(margin),
  });

  const handlePreview = async () => {
    if (!clientId) { setError('Selecciona un cliente'); return; }
    if (scope === 'ACCOUNT' && !accountLabel) { setError('Selecciona una cuenta'); return; }
    setError('');
    setLoading(true);
    setPreview(null);
    try {
      const data = await previewCostExport(buildRequest());
      setPreview(data);
    } catch {
      setError('No se pudo generar la previsualización. Verifica el rol y los permisos del cliente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!clientId) { setError('Selecciona un cliente'); return; }
    setError('');
    setDownloading(true);
    try {
      const result = await generateCostExport(buildRequest());
      // Abrir la URL prefirmada para descargar el Excel
      window.open(result.downloadUrl, '_blank');
    } catch {
      setError('No se pudo generar el Excel.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout onNavigate={onNavigate}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: '#1B2B6B' }}>Reporte de Costos</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Genera un Excel ejecutivo de costos por cliente, organización o cuenta específica.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuración */}
        <Card>
          <CardHeader title="Configuración" />
          {error && <Alert type="error" message={error} className="mb-4" />}

          <div className="space-y-4">
            {/* Cliente */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cliente</label>
              {clientsLoading ? (
                <Spinner />
              ) : (
                <select
                  value={clientId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setClientId(id);
                    setPreview(null);
                    setAccountLabel('');
                    // Precargar el margen configurado del cliente (editable).
                    const c = clients.find((x) => x.id === id);
                    setMargin(c?.marginPercentage != null ? String(c.marginPercentage) : '');
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 bg-white"
                >
                  <option value="">Selecciona un cliente…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Alcance */}
            {selectedClient && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Alcance</label>
                <div className="flex flex-col gap-1.5">
                  {hasMpa && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={scope === 'ORG'} onChange={() => setScope('ORG')} />
                      Organización (todas las cuentas)
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" checked={scope === 'CLIENT'} onChange={() => setScope('CLIENT')} />
                    Cliente ({accounts.length} cuenta{accounts.length !== 1 ? 's' : ''} configurada{accounts.length !== 1 ? 's' : ''})
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="radio" checked={scope === 'ACCOUNT'} onChange={() => setScope('ACCOUNT')} />
                    Cuenta específica
                  </label>
                </div>
              </div>
            )}

            {/* Cuenta específica */}
            {selectedClient && scope === 'ACCOUNT' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cuenta</label>
                <select
                  value={accountLabel}
                  onChange={(e) => setAccountLabel(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 bg-white"
                >
                  <option value="">Selecciona…</option>
                  {accounts.filter((a) => a.roleArn?.trim()).map((a) => (
                    <option key={a.label} value={a.label}>{a.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Periodo */}
            {selectedClient && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Periodo</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as 'previous' | 'current')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 bg-white"
                >
                  <option value="previous">Mes anterior (cerrado)</option>
                  <option value="current">Mes actual (a la fecha)</option>
                </select>
              </div>
            )}

            {/* Margen (%) — precargado del cliente, editable para este reporte */}
            {selectedClient && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Margen de ganancia (%)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={margin}
                  onChange={(e) => setMargin(e.target.value)}
                  placeholder="Sin margen"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 bg-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {selectedClient.marginPercentage != null
                    ? `Margen configurado del cliente: ${selectedClient.marginPercentage}%. Puedes ajustarlo para este reporte.`
                    : 'Este cliente no tiene margen configurado. Déjalo vacío para costo sin margen.'}
                </p>
              </div>
            )}

            {/* Acciones */}
            {selectedClient && (
              <div className="flex gap-2 pt-2">
                <Button variant="secondary" onClick={handlePreview} loading={loading} className="flex-1">
                  <EyeIcon className="h-4 w-4 mr-1.5" />
                  Previsualizar
                </Button>
                <Button onClick={handleDownload} loading={downloading} className="flex-1">
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                  Descargar Excel
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Previsualización */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader title="Previsualización" />
            {loading ? (
              <div className="py-12 flex justify-center"><Spinner size="lg" /></div>
            ) : !preview ? (
              <div className="py-12 text-center text-gray-400">
                <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Configura el reporte y presiona "Previsualizar"</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)' }}>
                  <div>
                    <p className="text-sm font-semibold text-green-800">{preview.scopeLabel}</p>
                    <p className="text-xs text-gray-500">
                      {preview.periodLabel}
                      {preview.marginApplied > 0 && ` · margen aplicado +${preview.marginApplied}%`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Costo total</p>
                    <p className="text-2xl font-bold text-green-600">${fmt(preview.totalCost)}</p>
                  </div>
                </div>

                {preview.byAccount.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Por cuenta</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-honne-light">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-honne-navy uppercase">Cuenta</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-honne-navy uppercase">Account ID</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-honne-navy uppercase">Costo</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {preview.byAccount.map((a) => (
                            <tr key={a.accountId}>
                              <td className="px-3 py-2">{a.accountName || a.accountId}</td>
                              <td className="px-3 py-2 font-mono text-xs text-gray-500">{a.accountId}</td>
                              <td className="px-3 py-2 text-right font-medium">${fmt(a.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Por servicio (top 15)</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-honne-light">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-honne-navy uppercase">Servicio</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-honne-navy uppercase">Costo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.byService.slice(0, 15).map((s) => (
                          <tr key={s.service}>
                            <td className="px-3 py-2">{s.service}</td>
                            <td className="px-3 py-2 text-right font-medium">${fmt(s.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
